import { WebSocketStatusEnum } from '../../enums/WebSocketStatusEnum';
import { useAppStore } from '../../store/store';

/** Chat composer/disclaimer phase — collapses yellow-dot hub states into one UX bucket. */
export type ChatRealtimePhase = 'connected' | 'connecting' | 'disconnected';

/** Map EventHub status → chat UX phase (amber connecting/reconnecting/closing share one phase). */
export function toChatRealtimePhase(status: WebSocketStatusEnum): ChatRealtimePhase {
  switch (status) {
    case WebSocketStatusEnum.Open:
      return 'connected';
    case WebSocketStatusEnum.Connecting:
    case WebSocketStatusEnum.Reconnecting:
    case WebSocketStatusEnum.Closing:
      return 'connecting';
    default:
      return 'disconnected';
  }
}

/**
 * Theme-token Tailwind classes for the header realtime status dot
 * (connected green / connecting·reconnecting yellow+pulse / disconnected red).
 */
export function webSocketConnectionToneClass(status: WebSocketStatusEnum): string {
  switch (status) {
    case WebSocketStatusEnum.Open:
      return 'bg-green';
    case WebSocketStatusEnum.Connecting:
    case WebSocketStatusEnum.Reconnecting:
    case WebSocketStatusEnum.Closing:
      return 'bg-yellow animate-pulse';
    default:
      return 'bg-red';
  }
}

/** i18n key for aria-label / title on the status dot. */
export function webSocketConnectionLabelKey(status: WebSocketStatusEnum): string {
  switch (status) {
    case WebSocketStatusEnum.Open:
      return 'realtime.connected';
    case WebSocketStatusEnum.Connecting:
    case WebSocketStatusEnum.Closing:
      return 'realtime.connecting';
    case WebSocketStatusEnum.Reconnecting:
      return 'realtime.reconnecting';
    default:
      return 'realtime.disconnected';
  }
}

/** Current EventHub SignalR status from the app store. */
export function getWebSocketConnectionStatus(): WebSocketStatusEnum {
  return useAppStore.getState().webSocket.status;
}

/**
 * Subscribe to EventHub connection status changes.
 * @returns Unsubscribe function.
 */
export function subscribeWebSocketConnectionStatus(
  listener: (status: WebSocketStatusEnum) => void,
): () => void {
  return useAppStore.subscribe((state, prevState) => {
    if (state.webSocket.status !== prevState.webSocket.status) {
      listener(state.webSocket.status);
    }
  });
}
